/**
 * rother3 companion — storage wrapper
 * Handles: IndexedDB for audits, localStorage for preferences
 */
import { auditFields } from './audit-shape.js';

const DB_NAME = 'rother3-companion';
const DB_VERSION = 1;
const AUDITS_STORE = 'audits';

/**
 * Open the IndexedDB database.
 *
 * Three defects were fixed here, all of them silent:
 *
 * 1. **`onblocked` was not handled at all.** When another tab holds an older version open,
 *    the upgrade cannot proceed, `onupgradeneeded` waits, and — with no `onblocked`
 *    handler — this promise **never settles**. The caller waits forever on a page that is
 *    simply not going to work, with nothing on screen to say why. It now rejects with a
 *    message naming the cause.
 * 2. **The connection was never closed.** Every function opened its own and leaked it.
 * 3. **Writes resolved on the request, not the transaction** — see `runInTransaction`.
 *
 * @returns {Promise<IDBDatabase>}
 */
export function openDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(AUDITS_STORE)) {
        db.createObjectStore(AUDITS_STORE, { keyPath: 'id', autoIncrement: true });
      }
    };

    request.onsuccess = () => resolve(request.result);

    request.onerror = () => reject(request.error);

    /**
     * Fired when another connection holds an older version open.
     *
     * Without this the promise hangs: `onupgradeneeded` cannot finish while another tab
     * has the old database open, so neither `onsuccess` nor `onerror` ever runs. Rejecting
     * turns an unexplained hang into a message a user can act on — close the other tab.
     */
    request.onblocked = () =>
      reject(
        new Error(
          'rother3 could not update its local database because another rother3 tab is ' +
            'open. Close the other tab and try again. Nothing was lost.'
        )
      );
  });
}

/**
 * Run a transaction and settle on the **transaction**, not on the request.
 *
 * This is the fix that matters. `store.add()` firing `onsuccess` only means the request
 * was queued and the key was assigned — the data is not durable until the transaction
 * commits. A transaction can still abort after that: a quota error on a later part of the
 * write, a constraint, or the tab closing.
 *
 * The old code resolved on `addRequest.onsuccess`, so the app called
 * `showStatus('Imported: …', 'success')` on a write that had not committed. The user was
 * told their audit was saved when it might not be, and then reloaded the page and found it
 * gone.
 *
 * The connection is closed in `finally`, because every caller opened its own and none of
 * them closed it.
 *
 * @param {string} mode - 'readonly' or 'readwrite'
 * @param {(store: IDBObjectStore) => IDBRequest} work - what to do with the store
 * @returns {Promise<any>} the request's result, once the transaction has committed
 */
function runInTransaction(mode, work) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        let result;
        let tx;
        try {
          tx = db.transaction(AUDITS_STORE, mode);
        } catch (err) {
          db.close();
          reject(err);
          return;
        }

        tx.oncomplete = () => {
          db.close();
          resolve(result);
        };
        // An abort is a failure even if the request itself succeeded. This is the case the
        // old code reported as success.
        tx.onabort = () => {
          db.close();
          reject(tx.error ?? new Error('The local database rejected the change.'));
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error ?? new Error('The local database could not complete the change.'));
        };

        try {
          const request = work(tx.objectStore(AUDITS_STORE));
          request.onsuccess = () => {
            result = request.result;
          };
          request.onerror = () => {
            // Letting the transaction abort carries the real reason; rejecting here too
            // would leave the abort handler to reject a second time.
            result = undefined;
          };
        } catch (err) {
          db.close();
          try {
            tx.abort();
          } catch {
            /* already finishing */
          }
          reject(err);
        }
      })
  );
}

/**
 * Store an audit in IndexedDB.
 *
 * Resolves only once the transaction has **committed**. See `runInTransaction` for why
 * that is not the same thing as the write request succeeding.
 *
 * @param {object} auditData - the audit data to store
 * @returns {Promise<number>} the stored record id
 */
export function storeAudit(auditData) {
  return runInTransaction('readwrite', (store) =>
    store.add({
      ...auditData,
      importedAt: new Date().toISOString(),
    })
  );
}

/**
 * Store many audits in **one** transaction.
 *
 * ## Why this exists
 *
 * Bulk import makes the one-at-a-time path unusable. `storeAudit` opens a connection, runs a
 * transaction, waits for it to commit, and closes the connection — and IndexedDB deliberately
 * yields to the event loop between transactions so the page can paint. So 137 imports is 137
 * round trips *with a frame rendered between each*, which is not a slow import, it is a
 * progress bar that never arrives: the tab looks frozen and the user closes it mid-import.
 *
 * One transaction for the whole archive removes both costs. The yield happens once.
 *
 * ## All or nothing, deliberately
 *
 * This does **not** write what it can and report the rest as failures. A partial import of a
 * ZIP leaves a set of businesses that looks complete — every one of them renders, every one of
 * them compares — while being silently missing whichever files happened to fail. The user then
 * reads a comparison over 90 businesses believing it covers 137, and nothing on the page says
 * otherwise. That is the project's worst failure class, so the transaction is atomic: if any
 * record fails to write, none are written and the whole import is reported as failed.
 *
 * The caller keeps per-file *parse* results, because a JSON file with a typo in it is the
 * user's problem to fix and naming it is more useful than aborting everything else.
 *
 * @param {object[]} auditsList - audits to store
 * @returns {Promise<number[]>} the stored ids, in input order
 */
export function storeAudits(auditsList) {
  if (!Array.isArray(auditsList) || auditsList.length === 0) {return Promise.resolve([]);}

  const importedAt = new Date().toISOString();
  const ids = [];

  return runInTransaction('readwrite', (store) => {
    // The last request is returned so the transaction's completion is awaited, but every
    // request is awaited inside as well: a later request can resolve before an earlier one,
    // and pushing into `ids` in completion order would misalign the ids with their audits.
    let last = null;
    auditsList.forEach((auditData, index) => {
      const request = store.add({ ...auditData, importedAt });
      request.onsuccess = () => {
        ids[index] = request.result;
      };
      last = request;
    });
    return last ?? store.get(1);
  }).then(() => ids);
}

/**
 * Delete every stored audit, in one transaction.
 *
 * "Remove all" is absent from the UI today and belongs to a browser's own storage controls
 * (`indexedDB.deleteDatabase`), which is a place a non-technical owner will not find. Having
 * the operation here means the confirmation dialog can state exactly what it destroys.
 *
 * @returns {Promise<void>}
 */
export function deleteAllAudits() {
  return runInTransaction('readwrite', (store) => store.clear()).then(() => undefined);
}

/**
 * Get all audits, newest import first.
 *
 * @returns {Promise<object[]>}
 */
export function getAllAudits() {
  return runInTransaction('readonly', (store) => store.getAll()).then((audits) => {
    const list = audits || [];
    // Invalid dates sort as NaN and leave the order unchanged, which is the right
    // behaviour: a record with an unreadable timestamp keeps its stored position rather
    // than jumping to the top or the bottom on a comparison that returns NaN.
    list.sort((a, b) => new Date(b.importedAt) - new Date(a.importedAt));
    return list;
  });
}

/**
 * One record per **business**, newest import winning.
 *
 * ## Why this exists
 *
 * `getAllAudits()` returns one row per *import*, and a re-import is the normal case: you open
 * the same listing again a week later to check whether anything moved, and that is a new
 * record. But nothing deduplicated them, so the same business appeared three or four times —
 * visible on the live site as `Single Fin Bali 14,280 / 14,258 / 14,257 / 361 / 362` on five
 * consecutive chart rows, and as `Single Fin Bali` five times in one ranking.
 *
 * That is not merely untidy. It **inflates the field**: a rating median computed over five
 * copies of one cafe is not a median over the market, and a benchmark that reports "1st of 13"
 * when there are 9 real businesses is a claim about arithmetic the reader cannot check. The
 * numbers were individually correct and the conclusion was false.
 *
 * ## The rule
 *
 * Keyed on `placeId`, which is Google's own stable identifier for a listing — the one field
 * that is genuinely constant across re-visits. **Newest import wins**, because that is the
 * freshest read of the listing.
 *
 * Two deliberate exceptions, because deleting data is worse than showing it:
 *
 * - A record with **no readable `placeId`** is kept as its own row. It cannot be proven to be
 *   a duplicate of anything, and dropping it would silently lose an audit the user has.
 * - A record whose `placeId` is only whitespace or a placeholder is treated as unreadable, for
 *   the same reason.
 *
 * ## What is *not* deleted
 *
 * The older duplicates stay in IndexedDB. This is a **view** over them, not a deletion — the
 * recent-audits list on the import page still shows every import, so a reader who wants the
 * older capture can still reach it. Collapsing them here means the *analysis* stops
 * double-counting, which is where the false claim was.
 *
 * @param {object[]} audits - newest first, as `getAllAudits()` returns
 * @returns {{fields: object[], collapsed: Map<string, object>}}
 */
export function dedupeByPlace(audits) {
  const fields = [];
  const collapsed = new Map();

  for (const audit of audits) {
    const f = auditFields(audit);
    const key = typeof f.placeId === 'string' ? f.placeId.trim() : '';

    // No usable identity. Kept, because "we could not tell" is not "it is the same one".
    if (key === '') {
      fields.push(f);
      continue;
    }

    const seen = collapsed.get(key);
    if (seen === undefined) {
      collapsed.set(key, f);
      fields.push(f);
      continue;
    }

    /**
     * A genuine repeat of a business we already have.
     *
     * Recorded rather than dropped silently, so the caller can *state* that a field is
     * smaller than the import count. A silently narrowed field is the same defect as an
     * inflated one: the reader believes a denominator nobody computed.
     */
    seen.duplicateCount = (seen.duplicateCount ?? 1) + 1;
  }

  return { fields, collapsed };
}

/**
 * Get a single audit by id.
 *
 * @param {number} id
 * @returns {Promise<object|undefined>}
 */
export function getAudit(id) {
  return runInTransaction('readonly', (store) => store.get(id));
}

/**
 * Delete an audit by id.
 *
 * Resolves on commit, so a caller that reports "Deleted" is not racing an abort.
 *
 * @param {number} id
 * @returns {Promise<void>}
 */
export function deleteAudit(id) {
  return runInTransaction('readwrite', (store) => store.delete(id)).then(() => undefined);
}

/**
 * localStorage helpers for preferences
 */
export const prefs = {
  get(key, defaultValue = null) {
    try {
      const value = localStorage.getItem(`rother3-${key}`);
      return value ? JSON.parse(value) : defaultValue;
    } catch {
      return defaultValue;
    }
  },

  set(key, value) {
    try {
      localStorage.setItem(`rother3-${key}`, JSON.stringify(value));
    } catch (err) {
      console.error('[rother3] prefs.set error:', err);
    }
  },

  remove(key) {
    localStorage.removeItem(`rother3-${key}`);
  },
};

/**
 * Read a preference **synchronously**, for code that runs before or during first paint.
 *
 * `prefs.get` is already synchronous, so this is mostly a named alias — but naming it matters
 * at the call site. The theme reader runs inside an inline `<head>` script, and a reader
 * reaching for `prefs` there has to reason about whether importing the module is even possible
 * that early. A function whose name says "safe before first paint" does not make the reader
 * stop and think.
 *
 * Kept as a function rather than a direct `localStorage` call because `localStorage` **throws**
 * in a private-mode browser in some engines, and a throw inside the head script would leave
 * the page with no theme at all. `prefs.get` swallows it and returns the default.
 */
export function getPref(key, defaultValue = null) {
  return prefs.get(key, defaultValue);
}

/**
 * Which business is "my business" — the left-hand column of every comparison.
 *
 * ## Why this exists rather than reusing the extension's anchor
 *
 * The extension has its own `myBusinessAnchor` in `chrome.storage.local`, and the export
 * carries an `anchor` block. Neither is usable here: the companion has no extension
 * storage access, and the export's `anchor` is a *snapshot of the extension's* anchor at
 * export time, which is the wrong business as soon as the user compares two exports from
 * two different sessions. The companion's anchor is a decision made **here**, in this
 * browser, about **these** audits.
 *
 * `localStorage` rather than IndexedDB: it is one small key, it is synchronous, and
 * `prefs` already fails soft — a private-mode browser that refuses `localStorage` gets
 * `null` and the comparison section explains itself, rather than the page breaking.
 *
 * Stored as a `placeId` because it is the only identifier a business keeps across
 * re-extractions. A name would change when the owner renames the listing, silently
 * unanchoring the comparison, and two branches of the same business share a name but not a
 * place.
 */
export const anchor = {
  /** The anchored business's `placeId`, or `null`. */
  get() {
    const value = prefs.get('anchor-place-id', null);
    return typeof value === 'string' && value.length > 0 ? value : null;
  },

  set(placeId) {
    if (typeof placeId === 'string' && placeId.length > 0) {
      prefs.set('anchor-place-id', placeId);
    } else {
      prefs.remove('anchor-place-id');
    }
  },
};
